import clsx from 'clsx';
import React, { FC, useEffect, useRef } from 'react';

import Image from '@/components/Image';
import cn from '@/helpers/cn';
import { useLazyVideoSource } from '@/hooks/useLazyVideoSource';
import { useViewportVideosPlayback } from '@/hooks/useViewportVideosPlayback';
import { gsap } from '@/lib/gsap';
import { StrapiImage, StrapiVideo } from '@/types/common';

import css from './style.module.scss';

interface Props {
	className?: string;
	children: string;
	preview?: StrapiImage | StrapiVideo;
	invertColors?: boolean;
	onClick?: () => void;
}

const BookACallButton: FC<Props> = ({ className, children, preview, invertColors, onClick }) => {
	const topRef = useRef<HTMLSpanElement>(null);
	const botRef = useRef<HTMLSpanElement>(null);
	const tlRef = useRef<gsap.core.Timeline | null>(null);
	const iconWrapperRef = useRef<HTMLDivElement>(null);

	const isVideo = preview && 'mime' in preview && preview.mime.startsWith('video');

	const shouldLoadVideo = useLazyVideoSource(iconWrapperRef);
	useViewportVideosPlayback([iconWrapperRef]);

	useEffect(() => {
		const ctx = gsap.context(() => {
			const top = topRef.current;
			const bot = botRef.current;

			if (!top || !bot) return;

			const tl = gsap.timeline({ paused: true });

			tl.to(
				top,
				{
					yPercent: -100,
					opacity: 0,
					duration: 0.2,
					ease: 'power2.out'
				},
				0
			).fromTo(
				bot,
				{
					yPercent: 100,
					opacity: 0
				},
				{
					yPercent: 0,
					opacity: 1,
					duration: 0.2,
					ease: 'power2.out'
				},
				0.05
			);

			tlRef.current = tl;
		});

		return () => ctx.revert();
	}, []);

	const handleMouseEnter = () => {
		tlRef.current?.play();
	};

	const handleMouseLeave = () => {
		tlRef.current?.reverse();
	};

	return (
		<button
			className={clsx(css.button, css.gradientBorder, className, invertColors && css.invertColors)}
			type="button"
			onMouseEnter={handleMouseEnter}
			onMouseLeave={handleMouseLeave}
			onClick={onClick}
		>
			<div ref={iconWrapperRef} className={css.iconWrapper}>
				{isVideo ? (
					<video muted loop playsInline className={css.icon} width={48} height={48} preload="none">
						{shouldLoadVideo ? (
							<source
								src={preview.url ? preview.url : '/assets/video/mp4/yaro.mp4'}
								type="video/mp4"
							/>
						) : null}
					</video>
				) : (
					<Image
						preload={false}
						className={css.image}
						width={48}
						height={48}
						src={preview?.url ?? ''}
						alt={(preview && 'alternativeText' in preview && preview.alternativeText) || 'Book a call'}
					/>
				)}
			</div>

			<div className={cn(css.content)} data-text={children}>
				<span ref={topRef} className={css.top}>
					{children}
				</span>
				<span ref={botRef} className={css.bot}>
					{children}
				</span>
			</div>
		</button>
	);
};

export default BookACallButton;
